import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SendNotification.css';
import { toast } from 'react-toastify';
import { 
    fetchHomeData, 
    fetchItems, 
    fetchPassword, 
    getCurrentSheetStatus,
    updateSheetId,
    fetchMainCategories,
    fetchSubCategories
} from '../services/googleSheets.ts';

const ONESIGNAL_APP_ID = '9767eaaa-a8a4-4ebe-be8d-46bf912bada0';
const ONESIGNAL_REST_API_KEY = 'os_v2_app_s5t6vkviurhl5puni27zck5nucgzedk2v32usmnj3mfag5h2wvfvdjghwpqfjiocy6pqt64uobngzowgkwadauoqzm7lz722mwofjsi';

const SendNotification: React.FC = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [adminPassword, setAdminPassword] = useState('');
    const [sheetId, setSheetId] = useState('');
    const [itemId, setItemId] = useState('');
    const [items, setItems] = useState<any[]>([]);
    const [globalPassword, setGlobalPassword] = useState('');
    const [currentSheet, setCurrentSheet] = useState('');
    const [loadingSheet, setLoadingSheet] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>('');
    const [showDebug, setShowDebug] = useState<boolean>(false);

    // وظيفة لجلب البيانات الأولية
    const loadInitialData = async () => {
        try {
            setLoading(true);
            // الحصول على حالة الشيت الحالي
            const sheetStatus = getCurrentSheetStatus();
            setCurrentSheet(sheetStatus.sheetId);
            //console.log('Current sheet ID:', sheetStatus.sheetId);

            // جلب كلمة المرور العامة من الشيت الرئيسي
            try {
                const passwords = await fetchPassword();
                if (passwords && passwords.length > 0) {
                    setGlobalPassword(passwords[0].password);
                    //console.log('Global password loaded');
                } else {
                    console.error('Global password not found');
                }
            } catch (error) {
                console.error('Error fetching global password:', error);
            }

            // جلب بيانات الصفحة الرئيسية والكلمة السرية
            await loadHomeData();
            
            // جلب جميع العناصر من الشيت الحالي
            await loadItems();
        } catch (error) {
            console.error('Error loading initial data:', error);
            toast.error('حدث خطأ في تحميل البيانات الأولية', { autoClose: 500 });
        } finally {
            setLoading(false);
        }
    };

    // وظيفة لجلب بيانات الصفحة الرئيسية والكلمة السرية
    const loadHomeData = async () => {
        try {
            const homeData = await fetchHomeData();
            if (homeData.notificationPassword) {
                setAdminPassword(homeData.notificationPassword);
                //console.log('Admin password loaded');
            } else {
                console.error('Password not found in home data');
                toast.error('حدث خطأ في تحميل بيانات التحقق', { autoClose: 500 });
            }
        } catch (error) {
            console.error('Error loading home data:', error);
            toast.error('حدث خطأ في تحميل بيانات الصفحة الرئيسية', { autoClose: 500 });
        }
    };

    // وظيفة لجلب العناصر من الشيت الحالي
    const loadItems = async () => {
        try {
            const fetchedItems = await fetchItems('all');
            //console.log('Loaded items:', fetchedItems.length);
            setItems(fetchedItems);
            return fetchedItems;
        } catch (error) {
            console.error('Error loading items:', error);
            toast.error('حدث خطأ في تحميل العناصر', { autoClose: 500 });
            return [];
        }
    };

    // تحميل البيانات الأولية عند تحميل الصفحة
    useEffect(() => {
        loadInitialData();

        // الاستماع إلى تحديثات الشيت
        const handleSheetUpdate = () => {
            //console.log('Sheet ID updated event detected');
            loadHomeData();
            loadItems();
        };

        window.addEventListener('sheetIdUpdated', handleSheetUpdate);
        
        return () => {
            window.removeEventListener('sheetIdUpdated', handleSheetUpdate);
        };
    }, []);

    // تحديث الشيت فقط عند الخروج من الحقل
    const handleSheetIdBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        const newSheetId = e.target.value;
        
        // إذا كان الشيت هو "all" أو فارغا أو نفس الشيت الحالي، لا تقم بتحديث
        if (!newSheetId || newSheetId.toLowerCase() === 'all' || newSheetId === currentSheet) {
            return;
        }
        
        // تطبيق التغيير
        await updateSheetNow(newSheetId);
    };

    // تحديث قيمة الشيت في الحالة فقط
    const handleSheetIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSheetId(e.target.value);
    };

    // وظيفة تطبيق تغيير الشيت فورًا
    const updateSheetNow = async (newSheetId: string) => {
        if (loadingSheet) return;
        setLoadingSheet(true);
        
        try {
            toast.info('جاري تحديث البيانات من الشيت الجديد...', { autoClose: 500 });
            //console.log('Updating sheet ID to:', newSheetId);
            updateSheetId(newSheetId);
            setCurrentSheet(newSheetId);
            
            // إعادة تحميل البيانات من الشيت الجديد بعد فترة قصيرة
            // هذا التأخير مهم لضمان تحديث الشيت قبل محاولة جلب البيانات
            setTimeout(async () => {
                try {
                    await loadHomeData();
                    const newItems = await loadItems();
                    toast.success(`تم تحديث الشيت بنجاح (${newItems.length} عنصر)`, { autoClose: 500 });
                } catch (error) {
                    console.error('Error reloading data after sheet update:', error);
                    toast.error('تم تغيير الشيت ولكن حدث خطأ في تحميل البيانات الجديدة', { autoClose: 500 });
                } finally {
                    setLoadingSheet(false);
                }
            }, 500);
        } catch (error) {
            console.error('Error updating sheet:', error);
            toast.error('حدث خطأ أثناء تحديث الشيت', { autoClose: 500 });
            setLoadingSheet(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading || loadingSheet) return;
        
        setLoading(true);
        
        // الآن ال effectiveSheetId سيكون دائمًا currentSheet لأننا نحدث الشيت فورًا
        const effectiveSheetId = sheetId.toLowerCase() === 'all' ? 'all' : currentSheet;
        
        //console.log('Login attempt:', {
        //     sheetId: effectiveSheetId,
        //     itemId,
        //     password,
        //     adminPassword,
        //     globalPassword
        // });
        
        // التحقق من الحالة all للشيت وللعنصر
        if (effectiveSheetId.toLowerCase() === 'all' && itemId.toLowerCase() === 'all') {
            // التحقق من كلمة المرور العامة
            if (password === globalPassword) {
                setIsAuthenticated(true);
                setPassword('');
                toast.success('تم تسجيل الدخول بنجاح للإرسال لجميع المستخدمين', { autoClose: 500 });
            } else {
                toast.error('كلمة المرور غير صحيحة للإرسال العام', { autoClose: 500 });
            }
            setLoading(false);
            return;
        }
        
        // التحقق من العنصر في الشيت الحالي
        if (itemId.toLowerCase() === 'all') {
            // التحقق من كلمة مرور الشيت الحالي
            if (password === adminPassword) {
                setIsAuthenticated(true);
                setPassword('');
                toast.success('تم تسجيل الدخول بنجاح', { autoClose: 500 });
            } else {
                toast.error('كلمة المرور غير صحيحة', { autoClose: 500 });
            }
            setLoading(false);
        } else {
            // البحث عن العنصر بالرقم المدخل
            //console.log('Looking for item:', itemId);
            //console.log('Available items count:', items.length);
            
            const item = items.find(item => item.id === itemId.toString());
            //console.log('Found item:', item);
            
            if (!item) {
                toast.error('رقم العنصر غير صحيح أو غير موجود في الشيت الحالي', { autoClose: 500 });
                setLoading(false);
                return;
            }
            
            const notificationPassword = item.notificationPassword;
            //console.log('Comparing passwords:', { 
            //     entered: password, 
            //     stored: notificationPassword
            // });

            if (password === notificationPassword) {
                setIsAuthenticated(true);
                setPassword('');
                toast.success(`تم تسجيل الدخول بنجاح للعنصر ${item.title}`, { autoClose: 500 });
            } else {
                toast.error('كلمة المرور غير صحيحة للعنصر', { autoClose: 500 });
            }
            setLoading(false);
        }
    };

    // وظيفة لإضافة معلومات التصحيح
    const addDebugInfo = (info: string) => {
        setDebugInfo(prev => prev + '\n' + info);
    };

    // وظيفة لمسح معلومات التصحيح
    const clearDebugInfo = () => {
        setDebugInfo('');
    };

    // وظيفة لتبديل عرض معلومات التصحيح
    const toggleDebug = () => {
        setShowDebug(prev => !prev);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
    
        setLoading(true);
        clearDebugInfo();
        addDebugInfo(`Submission started at: ${new Date().toISOString()}`);
    
        try {
            let targetUsers;
            const effectiveSheetId = sheetId.toLowerCase() === 'all' ? 'all' : currentSheet;
    
            if (effectiveSheetId.toLowerCase() === 'all' && itemId.toLowerCase() === 'all') {
                targetUsers = { included_segments: ['All'] };
            } else if (itemId.toLowerCase() === 'all') {
                targetUsers = { included_segments: ['All'] };
            } else {
                try {
                    addDebugInfo(`Fetching followers for item ID: ${itemId}`);
                    const apiUrl = `https://taj.city/backend/api/onesignal/get_followers.php?item_id=${itemId}`;
                    addDebugInfo(`Using API URL: ${apiUrl}`);
    
                    const response = await fetch(apiUrl);
                    addDebugInfo(`API response status: ${response.status}`);
    
                    if (!response.ok) {
                        const errorText = await response.text();
                        addDebugInfo(`API Error: ${errorText}`);
                        throw new Error(`Failed to fetch followers: ${response.status} ${response.statusText}`);
                    }
    
                    const responseText = await response.text();
                    addDebugInfo(`Raw API Response: ${responseText}`);
    
                    let followerUserIds = [];
                    try {
                        followerUserIds = JSON.parse(responseText);
                        addDebugInfo(`Parsed follower user IDs: ${JSON.stringify(followerUserIds)}`);
                    } catch (parseError) {
                        addDebugInfo(`Error parsing JSON: ${parseError}`);
                        console.error('Error parsing JSON response:', parseError);
                        toast.error('تم استلام استجابة غير صالحة من الخادم', { autoClose: 500 });
                        setLoading(false);
                        return;
                    }
    
                    if (!Array.isArray(followerUserIds)) {
                        addDebugInfo(`Expected array but got: ${typeof followerUserIds}`);
                        console.error('Expected array of user IDs but got:', typeof followerUserIds);
                        toast.error('تنسيق الاستجابة من الخادم غير صحيح', { autoClose: 500 });
                        setLoading(false);
                        return;
                    }
    
                    if (!followerUserIds.length) {
                        addDebugInfo(`No followers found for item: ${itemId}`);
                        console.warn('No followers found for item:', itemId);
                        toast.error('لا يوجد متابعين لهذا العنصر', { autoClose: 500 });
                        setLoading(false);
                        return;
                    }
    
                    // التأكد من أن كل external_id هو سلسلة نصية
                    const externalUserIds = followerUserIds.map(userId => String(userId));
                    addDebugInfo(`External user IDs for OneSignal: ${JSON.stringify(externalUserIds)}`);
    
                    targetUsers = { include_external_user_ids: externalUserIds };
                } catch (error) {
                    addDebugInfo(`Error fetching followers: ${error}`);
                    console.error('Error fetching followers:', error);
                    toast.error('حدث خطأ أثناء جلب قائمة المتابعين', { autoClose: 500 });
                    setLoading(false);
                    return;
                }
            }
    
            // الحصول على معلومات العنصر من القائمة إذا كان متاحًا
            let itemUrl = "";
            let itemTitle = "";
            let subcategoryUrl = "";

            if (itemId && itemId.toLowerCase() !== 'all') {
                const itemInfo = items.find(item => item.id === itemId);
                if (itemInfo) {
                    itemUrl = itemInfo.url || itemInfo.title.toLowerCase().replace(/\s+/g, '-');
                    itemTitle = itemInfo.title;
                    
                    // محاولة العثور على URL للفئة الفرعية من خلال subSubCategoryId
                    try {
                        const allSubCategories = await fetchMainCategories();
                        for (const mainCategory of allSubCategories) {
                            const subCategories = await fetchSubCategories(mainCategory.id);
                            for (const subCategory of subCategories) {
                                const items = await fetchItems(subCategory.id);
                                const matchingItem = items.find(item => item.id === itemId);
                                if (matchingItem) {
                                    subcategoryUrl = subCategory.url || subCategory.title.toLowerCase().replace(/\s+/g, '-');
                                    break;
                                }
                            }
                            if (subcategoryUrl) break;
                        }
                    } catch (error) {
                        console.error('Error finding subcategory URL:', error);
                    }

                    if (!subcategoryUrl) {
                        // إذا لم نتمكن من العثور على URL للفئة الفرعية، استخدم "default"
                        subcategoryUrl = "default";
                    }
                }
            }

            // إنشاء URL لصفحة العنصر مع معلومات الشيت والعنصر
            let notificationUrl = window.location.origin;
            let deepLinkData = {};

            if (itemId && itemId.toLowerCase() !== 'all') {
                // إضافة URL صفحة العنصر
                notificationUrl = `${window.location.origin}/${subcategoryUrl}/${itemUrl}`;
                
                // إضافة بيانات مخصصة لتمرير معرف الشيت ومعرف العنصر
                deepLinkData = {
                    item_id: itemId,
                    sheet_id: effectiveSheetId,
                    subcategory_url: subcategoryUrl,
                    item_url: itemUrl,
                    open_item: true
                };
                
                addDebugInfo(`Created item URL: ${notificationUrl}`);
                addDebugInfo(`Deep link data: ${JSON.stringify(deepLinkData)}`);
            }
    
            // تنسيق بيانات الإشعار بالشكل المطلوب
            const notificationData = {
                app_id: ONESIGNAL_APP_ID,
                headings: {
                    en: title,
                    ar: title
                },
                contents: {
                    en: description,
                    ar: description
                },
                ...(targetUsers.included_segments ? { included_segments: targetUsers.included_segments } : {}),
                ...(targetUsers.include_external_user_ids ? {
                    included_segments: ["Active Subscriptions"],
                    filters: [
                        {
                            field: "tag",
                            key: "external_user_id",
                            relation: "=",
                            value: targetUsers.include_external_user_ids[0]
                        },
                        ...targetUsers.include_external_user_ids.slice(1).map(id => ({
                            operator: "OR",
                            field: "tag",
                            key: "external_user_id",
                            relation: "=",
                            value: id
                        }))
                    ]
                } : {}),
                name: "INTERNAL_CAMPAIGN_NAME",
                target_channel: "push",
                data: {
                    title: title,
                    body: description,
                    item_id: itemId,
                    sheet_id: effectiveSheetId,
                    ...deepLinkData
                },
                web_push_topic: "news",
                priority: 10,
                ttl: 86400,
                isAnyWeb: true,
                chrome_web_icon: '/logo192.png',
                chrome_web_badge: '/logo192.png',
                chrome_web_image: '/logo192.png',
                url: notificationUrl,
                web_buttons: [{
                    id: "open",
                    text: "عرض التفاصيل",
                    url: notificationUrl
                }],
                chrome_web_options: {
                    requireInteraction: true,
                    dir: "rtl"
                }
            };
    
            addDebugInfo(`OneSignal Payload: ${JSON.stringify(notificationData)}`);
    
            const response = await fetch('https://onesignal.com/api/v1/notifications', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${ONESIGNAL_REST_API_KEY}`
                },
                body: JSON.stringify(notificationData)
            });
    
            const responseData = await response.json();
            addDebugInfo(`OneSignal API Response: ${JSON.stringify(responseData)}`);
    
            if (!response.ok) {
                throw new Error(`Failed to send notification: ${JSON.stringify(responseData)}`);
            }
    
            if (responseData && responseData.recipients === 0) {
                console.warn('The notification was created but no recipients were targeted.');
                toast.warn('الإشعار تم إنشاؤه لكن لم يتم استهداف أي أجهزة', { autoClose: 1000 });
            } else {
                toast.success(`تم إرسال الإشعار بنجاح`, { autoClose: 1000 });
            }
    
            setTitle('');
            setDescription('');
    
        } catch (error) {
            console.error('Error sending notification:', error);
            toast.error('حدث خطأ أثناء إرسال الإشعار', { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="send-notification-page">
            <div className="back-button-container">
                <button onClick={() => navigate('/')} className="back-button">
                    <svg viewBox="0 0 24 24">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                    <span>القائمة الرئيسية</span>
                </button>
            </div>

            <div className="notification-container">
                <h1>إرسال إشعار جديد</h1>
                
                {!isAuthenticated ? (
                    <form onSubmit={handleLogin} className="login-form">
                        <div className="form-group">
                            <label>رقم الشيت</label>
                            <input
                                type="text"
                                value={sheetId}
                                onChange={handleSheetIdChange}
                                onBlur={handleSheetIdBlur}
                                placeholder={`أدخل رقم الشيت`}
                                disabled={loadingSheet}
                            />
                        </div>
                        <div className="form-group">
                            <label>رقم العنصر</label>
                            <input
                                type="text"
                                value={itemId}
                                onChange={(e) => setItemId(e.target.value)}
                                placeholder="أدخل رقم العنصر"
                                required
                                disabled={loadingSheet}
                            />
                        </div>
                        <div className="form-group">
                            <label>كلمة المرور</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="أدخل كلمة المرور"
                                required
                                disabled={loadingSheet}
                            />
                        </div>
                        <button 
                            type="submit" 
                            className="login-button"
                            disabled={loading || loadingSheet}
                        >
                            {loading ? 'جاري التحميل...' : (loadingSheet ? 'جاري تحديث الشيت...' : 'تسجيل الدخول')}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} className="notification-form">
                        <div className="form-group">
                            <label>عنوان الإشعار</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="أدخل عنوان الإشعار"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>وصف الإشعار</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="أدخل وصف الإشعار"
                                required
                            />
                        </div>

                        <button 
                            type="submit" 
                            className="send-button"
                            disabled={loading}
                        >
                            {loading ? 'جاري الإرسال...' : 'إرسال الإشعار'}
                        </button>
                    </form>
                )}

                {isAuthenticated && (title || description) && (
                    <div className="notification-preview">
                        <h2>معاينة الإشعار</h2>
                        <div className="preview-card">
                            <div className="preview-content">
                                <h3>{title || 'عنوان الإشعار'}</h3>
                                <p>{description || 'وصف الإشعار'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SendNotification;